import { useMemo } from "react";

import { RotateCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { INTEGRATION_SCOPES } from "../../constants";
import { useCrawlerPreferencesState } from "../../hooks/useCrawlerPreferencesState";
import { useUpdateCrawlerPreference } from "../../hooks/useUpdateCrawlerPreference";
import { CrawlerPreferenceSelector } from "../CrawlerPreferenceSelector";

import type { DeploymentWorkspace } from "../../hooks/useDeploymentWorkspaces";
import type {
  CrawlerPreferenceData,
  CrawlerPreferenceType,
} from "../../types/crawlerPreference";

const clearSelection = (
  preferences: CrawlerPreferenceType[],
): CrawlerPreferenceType[] =>
  preferences.map((preference) => ({
    ...preference,
    data: preference.data.map((item) => ({ ...item, status: false })),
  }));

interface ConfigureWorkspaceDialogProps {
  source: string;
  sourceLabel: string;
  parentVerifiableId: string;
  /*
  The account's folder tree, already fetched and known to be ready — the page gates this
  dialog on it, so there is no loading or error state to handle here.
  */
  parentPreference: CrawlerPreferenceData;
  workspace: DeploymentWorkspace;
  onClose: () => void;
}

export const ConfigureWorkspaceDialog = ({
  source,
  sourceLabel,
  parentVerifiableId,
  parentPreference,
  workspace,
  onClose,
}: ConfigureWorkspaceDialogProps) => {
  /*
  A workspace picks from what the platform account can see. An already configured one
  additionally has its own selection, which the API returns pre-merged onto that tree.

  Memoised because useCrawlerPreferencesState re-seeds from this object by identity — a
  fresh object every render would wipe the checkboxes as they are clicked.
  */
  const seededData = useMemo(
    () => ({
      ...parentPreference,
      preferences:
        workspace.integration?.preferences ??
        clearSelection(parentPreference.preferences ?? []),
    }),
    [parentPreference, workspace.integration],
  );

  const preferencesState = useCrawlerPreferencesState({
    crawlerPreferenceData: seededData,
  });

  const updatePreference = useUpdateCrawlerPreference({
    onSuccess: () => {
      toast.success(`${workspace.organization.name} updated`);
      onClose();
    },
    onError: (mutationError) => {
      toast.error(mutationError.message || "Failed to save folder selection");
    },
  });

  const handleSave = () => {
    updatePreference.mutate({
      source,
      verifiableId: parentVerifiableId,
      data: { ...seededData, preferences: preferencesState.preferencesForSave },
      integrationScope: INTEGRATION_SCOPES.ORGANIZATION,
      organizationUuid: workspace.organization.organization_uuid,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{workspace.organization.name}</DialogTitle>
          <DialogDescription>
            Choose what {sourceLabel} content this workspace crawls. Only the
            selected items are indexed for its users.
          </DialogDescription>
        </DialogHeader>

        <CrawlerPreferenceSelector source={source} state={preferencesState} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updatePreference.isPending}
            data-testid="configure-workspace-save"
          >
            {updatePreference.isPending ? (
              "Saving..."
            ) : (
              <>
                <RotateCw className="mr-1 h-4 w-4" />
                Save and crawl
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
